"use client";

import { rmcClient } from "@/lib/rmcClient";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";

function pick(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "—";
}

function fmtQty(v, digits = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

const ChallanPrint = forwardRef(function ChallanPrint({ challan }, ref) {
  const contentRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [freshChallan, setFreshChallan] = useState(null);
  const [meta, setMeta] = useState({ party: null, site: null, vehicle: null, grades: [] });

  useEffect(() => {
    (async () => {
      const c = (await rmcClient.getCompany()).data;
      setCompany(c);
    })();
  }, []);

  useEffect(() => {
    if (!challan) return;
    setFreshChallan(null);
    (async () => {
      const parties = (await rmcClient.listSalesParties()).data;
      const vehicles = (await rmcClient.listVehicles()).data;
      const grades = challan.party_id
        ? (await rmcClient.listGrades({ party_id: challan.party_id })).data
        : [];
      const p = parties.find((x) => x.id === challan.party_id);
      const site = p?.sites?.find((s) => s.id === challan.site_id);
      const v = vehicles.find((x) => x.id === challan.vehicle_id);
      setMeta({
        party: p || null,
        site: site || null,
        vehicle: v || null,
        grades,
      });
    })();
  }, [challan]);

  const printFn = useReactToPrint({
    contentRef,
    documentTitle: challan?.challan_no || "challan",
  });

  const prepareFreshData = async () => {
    if (!challan?.id) return;
    const full = (await rmcClient.getSalesChallan(challan.id)).data;
    flushSync(() => {
      setFreshChallan(full);
    });
  };

  useImperativeHandle(ref, () => ({
    openPrint: async () => {
      if (!challan) return;
      await prepareFreshData();
      await new Promise((resolve) => setTimeout(resolve, 0));
      printFn();
    },
  }));

  if (!challan) return <div ref={contentRef} className="hidden" />;

  const source = freshChallan || challan;
  const items = (source.items || []).map((line) => {
    const g = line?.grade || meta.grades.find((x) => x.id === line.grade_id);
    const qty = Number(line.qty_cum ?? line.qty);
    return {
      ...line,
      label: g?.name || line?.grade_name || "—",
      qty: Number.isFinite(qty) ? qty : 0,
    };
  });
  const totalCum = items.reduce((sum, line) => sum + line.qty, 0);

  const salesCompany = source?.sales_company || source?.company || null;
  const companySource = salesCompany || company || {};
  const companyName = pick(companySource?.name, companySource?.company_name);
  const companyAddress = pick(companySource?.address, companySource?.address_line1);
  const companyPhone = pick(
    companySource?.contact_phone,
    companySource?.mobile_no,
    companySource?.mobile,
    companySource?.mobile_number,
    companySource?.phone,
    companySource?.contact_no
  );
  const companyGst = pick(companySource?.gst_number, companySource?.gstin);
  const companyBlessing = pick(companySource?.blessing, companySource?.invoice_blessing);

  const partyObj = { ...(meta.party || {}), ...(source?.party || {}) };
  const siteObj = { ...(meta.site || {}), ...(source?.site || {}) };
  const vehicleObj = { ...(meta.vehicle || {}), ...(source?.vehicle || {}) };
  const partyName = pick(partyObj?.name);
  const partyAddress = pick(partyObj?.address, partyObj?.billing_address, partyObj?.address_line1);
  const partyPhone = pick(
    partyObj?.contact_no,
    partyObj?.phone,
    partyObj?.mobile_no,
    partyObj?.mobile,
    partyObj?.mobile_number
  );
  const partyEmail = pick(partyObj?.email, source?.party_email);
  const partyGst = pick(partyObj?.gst_number, partyObj?.gstin);
  const siteName = pick(siteObj?.name, source?.site_name);
  const batchNo = pick(source?.batch_no, source?.batch_number, source?.challan_batch_no);
  const vehicleNo = pick(
    vehicleObj?.registration_no,
    source?.vehicle_no,
    meta.vehicle
      ? `${meta.vehicle.registration_no}${meta.vehicle.alias ? ` (${meta.vehicle.alias})` : ""}`
      : undefined
  );

  const Copy = ({ copyLabel }) => (
    <section className="h-[138.5mm] border border-slate-700 p-3 text-[10px] leading-tight">
      {companyBlessing !== "—" && (
        <div className="mb-2 text-center text-[10px] italic text-slate-700">
          {companyBlessing}
        </div>
      )}
      <div className="mb-2 flex items-start justify-between border-b border-slate-600 pb-2">
        <div>
          <p className="text-sm font-bold">{companyName}</p>
          <p className="max-w-[320px] text-[10px] text-slate-700">{companyAddress}</p>
          <p className="text-[10px] text-slate-700">
            GST: {companyGst} | Phone: {companyPhone}
          </p>
        </div>
        <div className="text-right">
          <p className="text-base font-bold tracking-wide">SALES CHALLAN</p>
          <p className="mt-1 text-[10px] text-slate-700">{copyLabel}</p>
        </div>
      </div>

      <table className="mb-2 w-full border-collapse border border-slate-600">
        <tbody>
          <tr>
            <td className="w-28 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Challan No</td>
            <td className="border border-slate-600 px-2 py-1">{pick(source?.challan_no)}</td>
            <td className="w-20 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Date</td>
            <td className="w-28 border border-slate-600 px-2 py-1">{pick(source?.date)}</td>
            <td className="w-20 border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Vehicle</td>
            <td className="w-28 border border-slate-600 px-2 py-1">{vehicleNo}</td>
          </tr>
          <tr>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Site</td>
            <td className="border border-slate-600 px-2 py-1">{siteName}</td>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Driver</td>
            <td className="border border-slate-600 px-2 py-1">{pick(source?.driver_name)}</td>
            <td className="border border-slate-600 bg-slate-100 px-2 py-1 font-semibold">Batch No</td>
            <td className="border border-slate-600 px-2 py-1">{batchNo}</td>
          </tr>
        </tbody>
      </table>

      <table className="mb-2 w-full border-collapse border border-slate-600">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-600 px-2 py-1 text-left">Party Name</th>
            <th className="border border-slate-600 px-2 py-1 text-left">Party Address</th>
            <th className="border border-slate-600 px-2 py-1 text-left">Contact No</th>
            <th className="border border-slate-600 px-2 py-1 text-left">Email</th>
            <th className="border border-slate-600 px-2 py-1 text-left">GST</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-slate-600 px-2 py-1">{partyName}</td>
            <td className="border border-slate-600 px-2 py-1">{partyAddress}</td>
            <td className="border border-slate-600 px-2 py-1">{partyPhone}</td>
            <td className="border border-slate-600 px-2 py-1">{partyEmail}</td>
            <td className="border border-slate-600 px-2 py-1">{partyGst}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td className="border border-slate-600 px-2 py-1 text-right font-semibold">Total CUM</td>
            <td className="border border-slate-600 px-2 py-1 text-right font-semibold">
              {fmtQty(totalCum)}
            </td>
          </tr>
        </tfoot>
      </table>

      <table className="w-full border-collapse border border-slate-600">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-600 px-2 py-1 text-left">Description</th>
            <th className="border border-slate-600 px-2 py-1 text-right">Quantity (CUM)</th>
          </tr>
        </thead>
        <tbody>
          {items.map((line, i) => (
            <tr key={i}>
              <td className="border border-slate-600 px-2 py-1">{line.label}</td>
              <td className="border border-slate-600 px-2 py-1 text-right">{fmtQty(line.qty)}</td>
            </tr>
          ))}
          {Array.from({ length: Math.max(0, 7 - items.length) }).map((_, idx) => (
            <tr key={`empty-${idx}`}>
              <td className="h-6 border border-slate-600 px-2 py-1" />
              <td className="border border-slate-600 px-2 py-1" />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Received By</div>
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Checked By</div>
        <div className="h-12 border border-slate-600 p-2 text-[10px]">Authorized Signature</div>
      </div>
    </section>
  );

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed left-0 top-0 -z-10 opacity-0 h-0 w-0 overflow-hidden print:static print:z-50 print:h-auto print:w-auto print:opacity-100 print:overflow-visible"
    >
      <div ref={contentRef} className="bg-white text-black print:block">
        <div className="mx-auto h-[297mm] w-[210mm] p-3">
          <Copy copyLabel="ORIGINAL" />
          <Copy copyLabel="DUPLICATE" />
        </div>
      </div>
    </div>
  );
});

export default ChallanPrint;
